import styled from '@emotion/styled'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { ArrowOblique } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles, theme } from 'app/theme'
import { rgba } from 'emotion-rgba'
import React, { memo } from 'react'

export interface Props {
  label?: string
  image?: ImageProps
  title?: string
  URL?: string
}

export const Card = memo(function Card({ image, label, title, URL }: Props) {
  if (!image) {
    return null
  }
  return (
    <Container href={URL}>
      <Box>
        <Image {...image} />
        <Text dial={7}>
          {label ? <Label className="item-label">{label}</Label> : null}

          {title ? <Title>{title}</Title> : null}
        </Text>

        <Icon>
          <ArrowOblique />
        </Icon>
      </Box>
    </Container>
  )
})

const Box = styled.div`
  position: relative;
  overflow: hidden;
  width: 30.563vw;
  aspect-ratio: 1;

  &:before {
    content: '';
    width: 100%;
    height: 50%;
    background: linear-gradient(
      ${rgba(theme.colors.variants.neutralDark3, 0)},
      ${rgba(theme.colors.variants.neutralDark3, 0.8)}
    );
    backface-visibility: hidden;
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 1;
  }
  &:after {
    content: '';
    width: 100%;
    height: 100%;
    background: linear-gradient(
      ${rgba(theme.colors.variants.neutralDark3, 0)},
      ${rgba(theme.colors.variants.neutralDark3, 0.4)}
    );
    backface-visibility: hidden;
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 1;
    opacity: 0;
    transition: 0.6s ease-in-out;
  }

  img {
    transition: 0.6s ease-in-out;
    ${styles.img}
  }

  @media (max-width: 1199px) {
    width: 100%;
    aspect-ratio: 1.33;
  }
`
const Icon = styled.div`
  width: 3rem;
  height: 3rem;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.variants.primaryDark};
  z-index: 2;
  transition: 0.3s ease-in-out;

  svg {
    fill: ${({ theme }) => theme.colors.variants.neutralLight3};
    height: 0.625rem;
    width: 0.625rem;
  }
`

const Text = styled(FlexBox)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 3rem;
  padding: 0 3.333vw;
  z-index: 2;

  @media (max-width: 1199px) {
    left: 1.5rem;
    right: 1.5rem;
    bottom: 3rem;
  }
`

const Label = styled.span`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralLight3};
  display: inline-flex;
  align-items: center;
  margin-bottom: 0.75rem;

  &::before {
    content: '';
    display: block;
    background-color: ${theme.colors.variants.neutralDark2};
    height: 0.0625rem;
    width: 0;
    transition: 0.3s ease-in-out;
  }
`

const Title = styled.h4`
  ${styles.title.normal}

  color: ${({ theme }) => theme.colors.variants.neutralLight3};
`

const Container = styled.a`
  @media (min-width: 1200px) {
    display: inline-block;

    &:hover {
      ${Box} {
        img {
          transform: scale(1.1);
        }
      }
      ${Icon} {
        width: 4.028vw;
        height: 4.028vw;
      }
    }
  }

  @media (max-width: 1199px) {
    display: block;
    width: 100%;
  }
`
